import type { FormInstance } from "antd";
import type {
  HighValuePurchaseRequisitionFormValue,
  LowValuePurchaseRequisitionFormValue,
  RdRawMaterialRequisitionFormValue,
  RldReferenceProductRequisitionFormValue,
} from "../../type";
import { convertFileItemsToString } from "@/utils/file";
import { SrmPurchaseRequestDto } from "@/types/apis";
import { PurchaseRequestTypeMap } from "@/constants";
import { notifyWarning } from "@/utils/notification";

/**
 * 采购人员填写（高价值采购申请 High Value）
 */
export const makeVariableForHighValuePurchasingStaffApprove = async (
  form: FormInstance<HighValuePurchaseRequisitionFormValue>,
): Promise<Record<string, any>> => {
  const values = await form.validateFields();

  if (!assertSupplierListCompleted(values.supplierList)) return {};

  const params: Record<string, any> = {
    materialGroup: values.materialGroup,
    vendorComparisonSummaryFiles: convertFileItemsToString(
      values.vendorComparisonSummaryFiles,
    ),
  };

  if (values.supplierList && values.supplierList.length > 0) {
    params.supplierList = values.supplierList.map((item) => ({
      ...item,
      quotationFiles: convertFileItemsToString(item.quotationFiles),
    }));
  }
  return params;
};

/**
 * 采购人员填写（低价值采购申请 Low Value）
 */
export const makeVariableForLowValuePurchasingStaffApprove = async (
  form: FormInstance<LowValuePurchaseRequisitionFormValue>,
): Promise<Record<string, any>> => {
  const values = await form.validateFields();

  if (!assertSupplierListCompleted(values.supplierList)) return {};

  const params: Record<string, any> = {
    purchaseGroup: values.purchaseGroup,
    sourcingResultRemark: values.sourcingResultRemark,
    contractRequired: values.contractRequired,
    itemList: values.itemList?.map((item) => {
      return {
        ...item,
        deliveryDate: item.deliveryDate?.format("YYYY-MM-DD"),
        attachments: convertFileItemsToString(item.attachments),
      };
    }),
  };
  if (values.supplierList && values.supplierList.length > 0) {
    params.supplierList = values.supplierList.map((item) => ({
      ...item,
      quotationFiles: convertFileItemsToString(item.quotationFiles),
    }));
  }
  return params;
};
/**
 * 财务人员初审(高价值采购申请 High Value)
 */
export const makeVariableForHighValueFinancialFirstAudit = async (
  form: FormInstance<HighValuePurchaseRequisitionFormValue>,
): Promise<Record<string, any>> => {
  const values = await form.validateFields();

  return {
    expenseDeptNo: values.expenseDeptNo,
    expenseDeptName: values.expenseDeptName,
    costCenterNo: values.costCenterNo,
    glAccount: values.glAccount,
    consolidatedScoringFiles: convertFileItemsToString(
      values.consolidatedScoringFiles,
    ),
    investmentAnalysisFiles: convertFileItemsToString(
      values.investmentAnalysisFiles,
    ),
  };
};

/**
 * 财务人员初审(低价值采购申请 Low Value)
 */
export const makeVariableForLowValueFinancialFirstAudit = async (
  form: FormInstance<LowValuePurchaseRequisitionFormValue>,
): Promise<Record<string, any>> => {
  const values = await form.validateFields();

  return {
    itemList: values.itemList?.map((item) => {
      return {
        ...item,
        attachments: convertFileItemsToString(item.attachments),
      };
    }),
  };
};

/**
 * 财务人员初审
 */
export const makeVariableForNormalFinancialFirstAudit = async (
  form: FormInstance<LowValuePurchaseRequisitionFormValue>,
): Promise<Record<string, any>> => {
  return {};
};

export const makeRdRawMaterialRequisition = (
  values: RdRawMaterialRequisitionFormValue,
): SrmPurchaseRequestDto => {
  const params: SrmPurchaseRequestDto = {
    ...values,
    formType: PurchaseRequestTypeMap.RdRawMaterialRequisition,
    qualityStandard: convertFileItemsToString(values.qualityStandard),
    suggestedSupplierCoa: convertFileItemsToString(values.suggestedSupplierCoa),
    requirementsFiles: convertFileItemsToString(values.requirementsFiles),
    deliveryDate: values.deliveryDate?.format("YYYY-MM-DD"),
  };

  if (values.supplierId) {
    params.supplierList = [
      {
        id: values.supplierFormId,
        supplierId: values.supplierId,
        supplierCode: values.supplierCode,
        supplierName: values.supplierName,
        quantity: values.quantity,
        quantityUnit: values.quantityUnit,
        price: values.price,
        priceUnit: values.priceUnit,
        priceHkd: values.priceHkd,
        totalAmount: values.totalAmount,
        totalAmountUnit: values.totalAmountUnit,
        totalAmountHkd: values.totalAmountHkd,
        remarks: values.remarks,
        quotationFiles: convertFileItemsToString(values.quotationFiles),
      },
    ];
  }

  return params;
};

export const makeRldReferenceProductRequisition = (
  values: RldReferenceProductRequisitionFormValue,
): SrmPurchaseRequestDto => {
  const params: SrmPurchaseRequestDto = {
    ...values,
    formType: PurchaseRequestTypeMap.RldReferenceProductRequisition,
    suggestedSupplierCoa: convertFileItemsToString(values.suggestedSupplierCoa),
    requirementsFiles: convertFileItemsToString(values.requirementsFiles),
    deliveryDate: values.deliveryDate?.format("YYYY-MM-DD"),
  };
  if (values.supplierId) {
    params.supplierList = [
      {
        supplierId: values.supplierId,
        supplierCode: values.supplierCode,
        supplierName: values.supplierName,
        quantity: values.quantity,
        quantityUnit: values.quantityUnit,
        price: values.price,
        priceUnit: values.priceUnit,
        priceHkd: values.priceHkd,
        totalAmount: values.totalAmount,
        totalAmountUnit: values.totalAmountUnit,
        totalAmountHkd: values.totalAmountHkd,
        remarks: values.remarks,
        quotationFiles: convertFileItemsToString(values.quotationFiles),
      },
    ];
  }
  return params;
};

export const makeHighValuePurchaseRequisition = (
  values: HighValuePurchaseRequisitionFormValue,
): SrmPurchaseRequestDto => {
  const { supplierList, ...restValues } = values;
  const params: SrmPurchaseRequestDto = {
    ...restValues,
    formType: PurchaseRequestTypeMap.HighValuePurchaseRequisition,
    deliveryDate: values.deliveryDate?.format("YYYY-MM-DD"),
    budgetType: values.budgetType,
    meetingMinutes: convertFileItemsToString(values.meetingMinutes),
    attachments: convertFileItemsToString(values.attachments),
    complianceAssessment: convertFileItemsToString(values.complianceAssessment),
    vendorComparisonSummaryFiles: convertFileItemsToString(
      values.vendorComparisonSummaryFiles,
    ),
    consolidatedScoringFiles: convertFileItemsToString(
      values.consolidatedScoringFiles,
    ),
    investmentAnalysisFiles: convertFileItemsToString(
      values.investmentAnalysisFiles,
    ),
    contractFiles: convertFileItemsToString(values.contractFiles),
  };

  if (supplierList && supplierList.length > 0) {
    params.supplierList = supplierList.map((item) => ({
      ...item,
      quotationFiles: convertFileItemsToString(item.quotationFiles),
    }));
  }

  return params;
};
export const makeLowValuePurchaseRequisition = (
  values: LowValuePurchaseRequisitionFormValue,
  isDraft: boolean,
): SrmPurchaseRequestDto => {
  const { supplierList, ...restValues } = values;

  if (!isDraft && !assertItemListCompleted(restValues.itemList)) {
    return {};
  }

  const params: SrmPurchaseRequestDto = {
    ...restValues,
    formType: PurchaseRequestTypeMap.LowValuePurchaseRequisition,
    requirementsFiles: convertFileItemsToString(values.requirementsFiles),
    itemList: values.itemList?.map((item) => {
      return {
        ...item,
        deliveryDate: item.deliveryDate?.format("YYYY-MM-DD"),
        attachments: convertFileItemsToString(item.attachments),
      };
    }),
    contractFiles: convertFileItemsToString(values.contractFiles),
  };

  if (supplierList && supplierList.length > 0) {
    params.supplierList = supplierList.map((item) => ({
      ...item,
      quotationFiles: convertFileItemsToString(item.quotationFiles),
    }));
  }

  return params;
};

function assertItemListCompleted(
  itemList?: { applyReason: string | null }[] | null,
) {
  if (
    itemList === null ||
    !itemList?.length ||
    itemList.some((item) => !item.applyReason?.trim())
  ) {
    notifyWarning("请完善采购物品明细");
    return false;
  }
  return true;
}

function assertSupplierListCompleted(
  supplierList?: { supplierName: string }[] | null,
) {
  if (
    supplierList === null ||
    !supplierList?.length ||
    supplierList.some((item) => !item.supplierName?.trim())
  ) {
    notifyWarning("请完善寻源结果");
    return false;
  }
  return true;
}
